// TODO remove this depend on dHotjarTYPv2 flag results
/* eslint-disable max-lines */
import React, {useEffect, useCallback, useState} from 'react';

import {connect, useSelector} from 'react-redux';

import {FeatureWithAnalytics, useFeatureWithAnalytics} from 'components/feature';
import {REFERER_OFFER} from 'constants/analytics/mentionMe';
import {MentionMe} from 'desktop/components/mentionMe/mentionMe';
import {
    HOTJAR_SITE_ID,
    HOTJAR_VERSION,
    useHotjarSurvey,
} from 'desktop/hooks/useHotjarSurvey/useHotjarSurvey';
import {ApplicationStoreType} from 'desktop/store';
import {useCheckLuxuryItem} from 'hooks/useCheckLuxuryItem/useCheckLuxuryItem';
import useSearchParams from 'hooks/useSearchParams/useSearchParams';
import {flags} from 'libs/rollout';
import {localStore} from 'libs/storage/storage';
import {sendFSEventWithOrderId} from 'libs/success/helpers/helpers';
import {VoidFunctionType} from 'types/types';

import {ReviewsPopupsConnected} from './components/reviewsPopups/reviewsPopups';
import {SuccessContactLensesConnected} from './components/successContactLenses/successContactLenses';
import {SuccessPageContent} from './components/successPageContent/successPageContent';
import {SuccessPageRedesign} from './components/successPageRedesign/successPageRedesign';
import {fetchOrderedItems} from './store/actions/orderItems/orderItems';
import {
    WE_GOT_IT,
    WE_WILL_VERIFY_THE_PRESCRIPTION,
    YOU_WILL_RECEIVE_A_CONFIRMATION,
} from './store/reducers/orderItems/constants';
import {openPopupPayment} from './store/reducers/orderLast/orderLast';
import {setUpsellCustomError} from './store/reducers/upsells/upsells';
import {setUpsellTimer} from './store/reducers/upsellTimer/upsellTimer';
import {
    isOrderContainsSomeContactLenses,
    getStatus,
    isOrderContainsSubscriptionItems,
    getSuccessScreen,
} from './store/selectors/orderItems/orderItems';
import {
    getOrderId,
    getOrderLastData,
    hasUpgrades as hasUpgradesSelector,
    hasAccessories as hasAccessoriesSelector,
    getOrderState,
    getUpsellsState,
} from './store/selectors/selectors';
import {SuccessScreenType} from './types/orderItemsTypes';

export type PropsType = {
    message: string;
    hasUpgrades: boolean;
    hasAccessories: boolean;
    visiblePopup: boolean;
    isUpsellsPending: boolean;
    togglePaymentPopup: VoidFunctionType<boolean>;
    setUpsellError: VoidFunctionType<string>;
    orderId: string;
    setUpsellTimerAction: VoidFunctionType;
    hasCartContactLenses: boolean;
    fetchOrderedItems: (orderId: string) => void;
    isOrderedItemsPending: boolean;
    isSubscriptionItems: boolean;
    skeleton: React.ReactNode;
    screen: SuccessScreenType;
};

const getIsShouldShowMentionMe = ({
    isAllowedMentionMePopup,
    isHotjarExp,
    isLuxuryEnabled,
    isMentionMeBannerEnabled,
}: {
    isAllowedMentionMePopup: boolean;
    isHotjarExp: boolean;
    isLuxuryEnabled: boolean;
    isMentionMeBannerEnabled: boolean;
}): boolean => {
    return !isHotjarExp && isAllowedMentionMePopup && !isLuxuryEnabled && !isMentionMeBannerEnabled;
};

const getReviewsPopupsComponent = ({
    isAllowedReviewsPopups,
    isLuxuryEnabled,
}: {
    isAllowedReviewsPopups: boolean;
    isLuxuryEnabled: boolean;
}) => isAllowedReviewsPopups && !isLuxuryEnabled && <ReviewsPopupsConnected />;

/**
 * Success Page
 * @param {PropsType} props - Component props
 * @return {React.ReactElement} component
 */
// TODO remove this depend on dHotjarTYPv2 flag results
// eslint-disable-next-line max-statements
export const SuccessPage: React.FC<PropsType> = ({
    fetchOrderedItems: fetchItems,
    hasAccessories = true,
    hasCartContactLenses,
    hasUpgrades = true,
    isOrderedItemsPending,
    isSubscriptionItems,
    isUpsellsPending,
    message,
    orderId,
    screen,
    setUpsellError,
    setUpsellTimerAction,
    skeleton,
    togglePaymentPopup,
    visiblePopup,
}) => {
    const isLuxuryEnabled = useCheckLuxuryItem(flags.dLuxurySpecialExperience.isEnabled());
    const isMentionMeBannerEnabled = useFeatureWithAnalytics(
        'typMentionMeBanner',
        false,
        'desktop',
    );

    const orderStatus = useSelector(getOrderState).status;
    const [isAllowedReviewsPopups, setIsAllowedReviewsPopups] = useState(false);
    const [isAllowedMentionMePopup, setIsAllowedMentionMePopup] = useState(false);
    const isHotjarExp = useHotjarSurvey();

    const shouldShowMentionMe = getIsShouldShowMentionMe({
        isAllowedMentionMePopup,
        isHotjarExp,
        isLuxuryEnabled,
        isMentionMeBannerEnabled,
    });

    useEffect(() => {
        if (isHotjarExp)
            import('@hotjar/browser').then(mod => mod.default.init(HOTJAR_SITE_ID, HOTJAR_VERSION));
    }, [isHotjarExp]);

    useEffect(() => {
        if (!hasCartContactLenses) return () => {};

        const isRatingsPopupEligibleScreen = [
            YOU_WILL_RECEIVE_A_CONFIRMATION,
            WE_GOT_IT,
            WE_WILL_VERIFY_THE_PRESCRIPTION,
        ].includes(screen);
        const timer = setTimeout(() => {
            setIsAllowedReviewsPopups(isRatingsPopupEligibleScreen);
            setIsAllowedMentionMePopup(true);
        }, 4000);
        return () => clearTimeout(timer);
    }, [hasCartContactLenses, screen]);

    const params = useSearchParams();
    useEffect(() => {
        if (orderStatus !== 'fulfilled') {
            setUpsellTimerAction();
            localStore.remove('persist:checkoutPersistentStore');
        }
    }, [setUpsellTimerAction, orderStatus]);
    /**
     * Close payments popup
     */
    const closePopup = useCallback(() => {
        if (!isUpsellsPending) {
            togglePaymentPopup(false);
            setUpsellError('');
        }
    }, [isUpsellsPending, setUpsellError, togglePaymentPopup]);

    useEffect(() => {
        if (orderId !== '') fetchItems(orderId);
    }, [fetchItems, orderId]);

    useEffect(() => {
        sendFSEventWithOrderId('Order Completed', orderId);
    }, [orderId]);

    if (isOrderedItemsPending) return skeleton;

    return (
        <>
            {hasCartContactLenses ? (
                <div className="main">
                    <SuccessContactLensesConnected />
                    {getReviewsPopupsComponent({isAllowedReviewsPopups, isLuxuryEnabled})}
                </div>
            ) : (
                <div className="main">
                    <FeatureWithAnalytics
                        fallback={
                            <SuccessPageContent
                                closePopup={closePopup}
                                hasAccessories={hasAccessories}
                                hasUpgrades={hasUpgrades}
                                isInsurance={params.get('insurance') === 'true'}
                                isSubscriptionItems={isSubscriptionItems}
                                message={message}
                                orderId={orderId}
                                visiblePopup={visiblePopup}
                            />
                        }
                        flag="dTypUI"
                        platform="desktop"
                    >
                        <SuccessPageRedesign shipmentInfo={message} />
                    </FeatureWithAnalytics>
                    {getReviewsPopupsComponent({isAllowedReviewsPopups, isLuxuryEnabled})}
                </div>
            )}
            {shouldShowMentionMe && (
                <MentionMe
                    params={{
                        locale: 'en_GB',
                        situation: 'postpurchase',
                    }}
                    integratingType={REFERER_OFFER}
                />
            )}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    hasAccessories: hasAccessoriesSelector(store),
    hasCartContactLenses: isOrderContainsSomeContactLenses(store),
    hasUpgrades: hasUpgradesSelector(store),
    isOrderedItemsPending: getStatus(store) === 'pending',
    isSubscriptionItems: isOrderContainsSubscriptionItems(store),
    isUpsellsPending: getUpsellsState(store).status === 'pending',
    message: getOrderState(store).message,
    orderId: getOrderId(store),
    screen: getSuccessScreen(store),
    visiblePopup: Boolean(getOrderLastData(store).popupVisible),
});

const mapDispatchToProps = {
    fetchOrderedItems,
    setUpsellError: setUpsellCustomError,
    setUpsellTimerAction: setUpsellTimer,
    togglePaymentPopup: openPopupPayment,
};
export const SuccessPageConnected = connect(mapStateToProps, mapDispatchToProps)(SuccessPage);
